import { getApi } from "../services/apiServices";
import { postApi } from "../services/apiServices";
import { secureStorage } from "./encryption";

export const fetchCountriesEsim = async (
  count, page, setCountriesEsim, setError, setLoading, search = '', filter = '', setTotalCountries = null, currency = null, userChangedCurrency = false, language = 'en',
) => {
  try {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      count,
      page,
      filter,
      search,
    });

    // If user has changed currency, always use the saved currency from localStorage
    if (userChangedCurrency) {
      const savedCurrency = localStorage.getItem('selectedCurrency') || 'USD';
      params.append('currency', savedCurrency);
    }

    // Add language parameter
    params.append('lang', language);

    const response = await getApi(`/api/countries?${params.toString()}`);
        
        if (response?.status) {
            setCountriesEsim(response?.data?.countries || []);
            if (setTotalCountries && response?.data?.total) {
                setTotalCountries(response.data.total);
            } else if (setTotalCountries && response?.data?.total_countries) {
                setTotalCountries(response.data.total_countries);
            } else if (setTotalCountries && response?.data?.total_count) {
                setTotalCountries(response.data.total_count);
            }
        } else {
            setError(response?.msg || 'Failed to fetch countries esim');
        }
    } catch (error) {
        console.error('Error fetching countries:', error);        
        let errorMessage = 'Failed to fetch countries esim';
        if (error?.code === 'ERR_NETWORK') {
            errorMessage = 'Network error - please check your internet connection';
        } else if (error?.response?.status === 0) {
            errorMessage = 'CORS error - please try refreshing the page';
        } else if (error?.response?.status === 404) {
            errorMessage = 'API endpoint not found';
        } else if (error?.response?.status >= 500) {
            errorMessage = 'Server error - please try again later';
        } else if (error?.message) {
            errorMessage = error.message;
        }
        
        setError(errorMessage);
    } finally {
        setLoading(false);
    }
};

/**
 * Recalculate all cart items in the new currency and update secureStorage.
 * @param {string} currencyCode - The new currency code (e.g., 'USD', 'EUR')
 */
export const recalculateCartItemsInNewCurrency = async (currencyCode) => {
  const cart = secureStorage.getItem('checkoutSession') || [];
  
  if (cart.length === 0) {
    return;
  }
  
  console.log('Recalculating cart items in new currency:', currencyCode);
  const updatedCart = [];

  for (const item of cart) {
    let apiPayload = {
      esim: item.esim_count || 1,
      country_code: item.country_code || item.iso3,
      currency: currencyCode,
    };
    if (item.plan_type === 'Unlimited Plans') {
      apiPayload.days = item.days || 1;
      apiPayload.type = 'unlimited';
    } else {
      apiPayload.gb = item.gb || 1;
      apiPayload.lowest_rate = parseFloat(item.lowest_rate) || 6.99;
      apiPayload.type = item.plan_type === 'Data Bundles' ? 'bundle' : 'global';
    }

    try {
      const response = await postApi('/api/calculation-of-plans', apiPayload);
      if (response?.status) {
        const newPrice = parseFloat(response.data.final_total) || 0;
        item.price = newPrice;
        item.currency = currencyCode;
        item.api_response = response.data;
        item.calculated_at = new Date().toISOString();
        
        if (response.data.no_of_days) {
          item.days = response.data.no_of_days;
        }
        if (response.data.number_of_esims) {
          item.esim_count = response.data.number_of_esims;
        }
      } else {
        console.error('API call failed:', response?.message);
        item.currency = currencyCode;
      }
    } catch (e) {
      console.error('Error recalculating item in new currency:', e);
      item.currency = currencyCode;
    }
    updatedCart.push(item);
  }

  secureStorage.setItem('checkoutSession', updatedCart);
  
  window.dispatchEvent(new Event('cartUpdated'));
};

/**
 * Get the current plan configuration from the page
 */
const getCurrentPlanConfiguration = () => {
  const currentConfig = localStorage.getItem('currentPlanConfig');
  if (currentConfig) {
    try {
      return JSON.parse(currentConfig);
    } catch (e) {
      console.error('Error parsing current plan config:', e);
    }
  }
  
  const countryCode = window.location.pathname.split('/').pop() || 'USA';
  const activeTab = localStorage.getItem('activeTab') || 'Data Bundles';
  
  let config = {
    esim: 1,
    country_code: countryCode,
    currency: localStorage.getItem('selectedCurrency') || 'USD',
  };
  
  if (activeTab === 'Unlimited Plans') {
    config.days = 1;
    config.type = 'unlimited';
  } else {
    config.gb = 1;
    config.lowest_rate = 6.99;
    config.type = activeTab === 'Data Bundles' ? 'bundle' : 'global';
  }
  
  return config;
};